import { Button } from '@/app/common/components/button/Button'
import { Input } from '@/app/common/components/input/Input'
import Label from '@/app/common/components/Label'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { EditIconGeneral } from '@/app/common/icons/general'
import { parseRequestError } from '@/app/common/utilities'
import usePutSystemCatalogCategory from '@/queries/system_catalog/hooks/usePutSystemCatalogCategory'
import { SystemCatalogCategory } from '@/queries/system_catalog/system_catalog.schema'
import { Dialog, DialogContent, DialogTrigger } from '@/shad/ui/dialog'
import { RadioGroup, RadioGroupItem } from '@/shad/ui/radio-group'
import { useToast } from '@/shad/ui/toast/use-toast'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

// Define the Zod schema
const categorySchema = z.object({
  categoryName: z
    .string()
    .min(2, { message: 'Category name must be at least 2 characters long' }),
  status: z.enum(['Active', 'Inactive']),
})

// TypeScript type from the Zod schema
type CategoryFormValues = z.infer<typeof categorySchema>

interface Props {
  onAddCategorySuccess: () => void
  category: SystemCatalogCategory
}

function EditCategoryPopup(props: Props) {
  const { onAddCategorySuccess, category } = props
  const { toast } = useToast()

  const [open, setOpen] = useState(false)
  const createCategoryMutation = usePutSystemCatalogCategory({
    onSuccess: () => {
      toast({
        title: 'Category Update',
        description: 'The category has been updated successfully.',
        variant: 'default',
      })
      onAddCategorySuccess()
      setOpen(false)
    },
    onError: (error: unknown) => {
      const errorMessage = parseRequestError(error).message
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    },
  })

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<CategoryFormValues>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      categoryName: category.name,
      status: category.status,
    },
  })

  // Form submission handler
  const onSubmit = (data: CategoryFormValues) => {
    createCategoryMutation.mutate({
      id: category.id,
      name: data.categoryName,
      status: data.status,
    })
  }

  // Close handler
  const handleClose = () => {
    setOpen(false)
    reset()
  }

  return (
    <div>
      <Dialog
        open={open}
        onOpenChange={(value) => {
          setOpen(value)
        }}
      >
        <DialogTrigger>
          <Button
            onClick={(event) => {
              setOpen(true)
              event.stopPropagation()
            }}
            variant={'outline'}
          >
            <EditIconGeneral />
          </Button>
        </DialogTrigger>
        <DialogContent onClick={(event) => event.stopPropagation()}>
          <h2 className="font-medium">Edit Category</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-1">
            <div className="mb-4">
              <Label>Category Name</Label>
              <Input
                {...register('categoryName')}
                aria-invalid={errors.categoryName ? 'true' : 'false'}
              />
              {errors.categoryName && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.categoryName.message}
                </p>
              )}

              <div className="mt-4 mb-8">
                <Label>Status</Label>
                <Controller
                  control={control}
                  name="status"
                  render={({ field }) => {
                    return (
                      <div>
                        <RadioGroup
                          defaultValue={category.status || 'Active'}
                          className="flex flex-col space-y-1 mt-1"
                          onValueChange={(value) => {
                            field.onChange(value)
                          }}
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Active" id="active" />
                            <label htmlFor="active" className="text-sm">
                              Active
                            </label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Inactive" id="inactive" />
                            <label htmlFor="inactive" className="text-sm">
                              Inactive
                            </label>
                          </div>
                        </RadioGroup>
                        {errors.status && (
                          <p className="text-sm text-red-500 mt-1">
                            {errors.status.message}
                          </p>
                        )}
                      </div>
                    )
                  }}
                />
              </div>
            </div>
            <div className="space-x-2 flex">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <div>
                <SubmitButton
                  buttonTxt="Save changes"
                  status={createCategoryMutation.status}
                ></SubmitButton>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default EditCategoryPopup
